import { useCallback } from "react";
import { find } from "lodash";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Card } from "@radarly/models/cards";
import { getPrimaryMetricFromCard } from "@radarly/models/cards/metric";
import {
  DatavizHorizontalBarChart,
  updateDatavizOptions,
} from "@radarly/models/dataviz";

function getSortOptions(metric: Metric, locale: Locale) {
  const defaultOptions = [
    {
      value: "alpha",
      label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT_BY_NAME"),
    },
    {
      value: "default",
      label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT_BY_DEFAULT"),
    },
  ];
  return metric === Metric.Rating
    ? [
        {
          value: "bestRanking",
          label: tr(
            locale,
            "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT_BY_BEST_RANKING"
          ),
        },
        {
          value: "worstRanking",
          label: tr(
            locale,
            "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT_BY_WORST_RANKING"
          ),
        },
        ...defaultOptions,
      ]
    : [
        {
          value: "mainUnit",
          label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT_BY_VALUE"),
        },
        ...defaultOptions,
      ];
}

function getSortOrderByMetric(value: string, metric: Metric) {
  if (metric === Metric.Rating) {
    if (value === "bestRanking" || value === "worstRanking") {
      const order = value === "bestRanking" ? "desc" : "asc";
      return { sort: "mainUnit", order };
    }
  }
  return { sort: value, order: "desc" };
}

function isRatingMainUnit(
  sort: DatavizHorizontalBarChart["sort"],
  metric: Metric
) {
  return (!sort || sort === "mainUnit") && metric === Metric.Rating;
}

function getSortValueFromDatavizAndMetric(
  dataviz: DatavizHorizontalBarChart,
  metric: Metric
) {
  if (isRatingMainUnit(dataviz.sort, metric)) {
    return dataviz.order === "asc" ? "worstRanking" : "bestRanking";
  }
  return dataviz.sort || "mainUnit";
}

function getSortOption(
  metric: Metric,
  dataviz: DatavizHorizontalBarChart,
  locale: Locale
) {
  const sortOptions = getSortOptions(metric, locale);
  const value = getSortValueFromDatavizAndMetric(dataviz, metric);
  return {
    value,
    label: find(sortOptions, { value })?.label,
  };
}

export function useSort(
  card: Card,
  dataviz: DatavizHorizontalBarChart,
  setDataviz: (dataviz: DatavizHorizontalBarChart) => void
) {
  const metric = getPrimaryMetricFromCard(card);
  const locale = useCurrentUserLocale();
  const options = getSortOptions(metric, locale);
  const onChange = useCallback(
    ({ value }) => {
      const { sort, order } = getSortOrderByMetric(value, metric);
      const withSort = updateDatavizOptions(dataviz, "sort", sort);
      setDataviz(updateDatavizOptions(withSort, "order", order));
    },
    [dataviz, metric, setDataviz]
  );
  const option = getSortOption(metric, dataviz, locale);
  return {
    label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT"),
    options,
    onChange,
    option,
  };
}
